import dayjs from 'dayjs';
import { formatCareOfDisplay } from 'utils/formatCareOfDisplay';
import { getFormattedLocation } from 'utils/locationUtils';
import { fetchUserPreferencesWithApp, updateUserPreferencesWithApp } from 'utils/apiHelper';

export const IPD_TEMPLATE_STORAGE_KEY = 'ipd_report_templates';
export const IPD_TEMPLATE_PREFERENCES_KEY = 'ipd_report_templates';

export const IPD_TEMPLATE_VARIABLES = [
  { key: 'patient_name', label: 'Patient Name' },
  { key: 'patient_id', label: 'Patient ID' },
  { key: 'patient_age', label: 'Patient Age' },
  { key: 'patient_gender', label: 'Patient Gender' },
  { key: 'patient_relation', label: 'S/O D/O W/O' },
  { key: 'patient_address', label: 'Patient Address' },
  { key: 'patient_phone', label: 'Patient Phone' },
  { key: 'surgeon_name', label: 'Surgeon Name' },
  { key: 'admission_id', label: 'Admission ID' },
  { key: 'admission_date', label: 'Admission Date' },
  { key: 'discharge_date', label: 'Discharge Date' },
  { key: 'doctor_name', label: 'Doctor Name' },
  { key: 'department', label: 'Department' },
  { key: 'bed_number', label: 'Bed Number' },
  { key: 'room_name', label: 'Room Name' },
  { key: 'room_type', label: 'Room Type' },
  { key: 'floor_name', label: 'Floor Name' },
  { key: 'hospital_name', label: 'Hospital Name' },
  { key: 'today_date', label: "Today's Date" },
  { key: 'procedure_name', label: 'Operation / Procedure Name' },
  { key: 'anesthetist_name', label: 'Anesthetist Name' },
  { key: 'nurse_name', label: 'Nurse Name' },
  { key: 'relative_name', label: 'Relative Name' },
  { key: 'relative_relationship', label: 'Relationship with Patient' },
  { key: 'consent_datetime', label: 'Consent Date & Time' }
];

export const DEFAULT_IPD_DISCHARGE_TEMPLATE = {
  id: 'default-discharge-summary',
  name: 'Discharge Summary (default)',
  content: `<h2>IPD Discharge Summary</h2>
<p><strong>Patient Name:</strong> {{patient_name}}</p>
<p><strong>Patient ID:</strong> {{patient_id}}</p>
<p><strong>Age / Gender:</strong> {{patient_age}} / {{patient_gender}}</p>
<p><strong>Admission Date:</strong> {{admission_date}}</p>
<p><strong>Discharge Date:</strong> {{discharge_date}}</p>
<p><strong>Doctor:</strong> {{doctor_name}} ({{department}})</p>
<p><strong>Bed / Room:</strong> {{bed_number}} / {{room_name}} ({{room_type}})</p>
<p><strong>Floor:</strong> {{floor_name}}</p>
<h3>Final Diagnosis</h3>
<p><br></p>
<h3>Treatment Summary</h3>
<p><br></p>
<h3>Discharge Instructions</h3>
<p><br></p>
<h3>Follow-up</h3>
<p><br></p>`,
  created_at: null,
  is_builtin: true
};

export const DEFAULT_URSL_PCNL_CONSENT_TEMPLATE = {
  id: 'default-ursl-pcnl-consent-hi',
  name: 'URSL–PCNL Consent (Hindi)',
  content: `<h2 style="text-align:center">सहमति पत्र</h2>
<h3 style="text-align:center"><strong>CONSENT FOR URSL – PCNL (Urology Surgery)</strong></h3>
<p><br></p>
<p>1. मरीज का नाम : <strong>{{patient_name}}</strong> &nbsp;&nbsp;&nbsp; पुत्र/पुत्री/पत्नी : <strong>{{patient_relation}}</strong> &nbsp;&nbsp;&nbsp; उम्र : <strong>{{patient_age}}</strong></p>
<p>पता : <strong>{{patient_address}}</strong></p>
<p><br></p>
<p>2. मैं यह गुर्दे की नली में पथरी का ऑपरेशन ठीक करने (URSL) Dr. <strong>{{surgeon_name}}</strong> एवं उनके सहयोगियों के पूर्ण देखरेख में करवाने को राजी हूँ।</p>
<p><br></p>
<p>3. मुझे इस बीमारी एवं इसकी शल्य क्रिया के हर बिंदु (बीमारी की गंभीरता, शल्य क्रिया की जरूरत, इस पर आने वाला व्यय, इस क्रिया से होने वाले फायदे-नुकसान, इस में निहित Risk व Complications (इससे पड़ने वाले प्रभाव आदि) के विस्तृत जानकारी अपनी भाषा में समझा दी गई है।</p>
<p><br></p>
<p><strong>इस Operation में संभावित Complications इस प्रकार हैं :</strong></p>
<ol>
<li>पेशाब में खून आना (HEMATURIA)</li>
<li>पथरी की जगह से सरकने या छूटने पर दुबारा ऑपरेशन की आवश्यकता</li>
<li>पेशाब की नली, थैली, या गुर्दे का संक्रमण (INFECTION / SEPSIS)</li>
<li>ऑपरेशन के बाद दोबारा पथरी होने पर दोबारा ऑपरेशन की आवश्यकता</li>
<li>गुर्दे की नली में चोट (Ureteral Perforation / Injury / Avulsion)</li>
<li>पेशाब के रास्ते या गुर्दे की नली में सिकुड़न (URETHRAL / URETERIC STRICTURE)</li>
<li>पेशाब की नली में चोट</li>
<li>मरीज को बुखार होने या संक्रमण होने पर गुर्दे में पीठ से नली या पेशाब के रास्ते नली की की आवश्यकता (PCN / DJ STENTING)</li>
<li>आंत में चोट</li>
<li>पैर की नसों में रक्त का जमाव (DVT)</li>
<li>रक्त ज्यादा बहने या गुर्दे की नली फटने पर ऑपरेशन रोक कर दोबारा ऑपरेशन, या चीरा पद्धति से ऑपरेशन की आवश्यकता (CONVERSION TO OPEN)</li>
<li>पथरी का खोना (Lost Stone)</li>
<li>संक्रमण (Infection)</li>
</ol>
<p>मुझे अन्य विकल्प उपचारों जैसे OPEN URETEROLITHOTOMY / LAP URETEROLITHOTOMY आदि के बारे में भी बता दिया गया है। उपचार न होने की स्थिति में होने वाले परिणामों (जैसे बुखार होना, संक्रमण, पीड़ा, कैंसर) आदि के बारे में भी जानकारी दी गई है। ऑपरेशन के दौरान कुछ अन्य विकारों का पता जाना भी संभावित है जिसके लिये उचित उपचार की सहमति मैं सर्जन को देता/देती हूँ।</p>
<p><br></p>
<p>दिनांक : <strong>{{today_date}}</strong></p>
<p><br></p>
<p>मरीज / रिश्तेदार का हस्ताक्षर : _________________________</p>
<p>गवाह का हस्ताक्षर : _________________________</p>
<p>चिकित्सक का हस्ताक्षर : _________________________</p>`,
  created_at: null,
  is_builtin: true
};

export const BUILTIN_IPD_TEMPLATES = [];

const BUILTIN_TEMPLATE_IDS = new Set(BUILTIN_IPD_TEMPLATES.map((t) => t.id));

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-');

const calcAge = (patient) => {
  if (patient?.age && String(patient.age).trim()) return String(patient.age).trim();
  if (!patient?.dob) return '-';
  const years = dayjs().diff(dayjs(patient.dob), 'year');
  return years >= 0 ? String(years) : '-';
};

const buildPatientAddress = (patient) => {
  const location = getFormattedLocation(patient);
  if (location) return location;
  const parts = [patient?.village, patient?.home_pincode].filter(Boolean);
  return parts.length ? parts.join(', ') : '-';
};

const buildPatientCoreFields = (patient, hospital = {}) => ({
  patient_name: patient?.full_name || patient?.name || '-',
  patient_id: String(patient?.id || patient?.patient_id || '-'),
  patient_age: calcAge(patient),
  patient_gender: patient?.gender || '-',
  patient_relation: formatCareOfDisplay(patient) || patient?.careof || '-',
  patient_address: buildPatientAddress(patient),
  patient_phone: patient?.phone_number || patient?.phone || '-',
  hospital_name: hospital?.name || hospital?.hospital_name || '-',
  today_date: dayjs().format('DD/MM/YYYY')
});

/** Variable map from user/patient record only (no active IPD admission). */
export const buildPatientVariableMap = (patient, hospital = {}) => ({
  ...buildPatientCoreFields(patient, hospital),
  surgeon_name: '-',
  admission_id: '-',
  admission_date: '-',
  discharge_date: formatDate(dayjs()),
  doctor_name: '-',
  department: '-',
  bed_number: '-',
  room_name: '-',
  room_type: '-',
  floor_name: '-',
  procedure_name: '-',
  anesthetist_name: '-',
  nurse_name: '-',
  relative_name: '-',
  relative_relationship: '-',
  consent_datetime: formatDate(dayjs())
});

export const buildAdmissionVariableMap = (admission, hospital = {}) => {
  const patient = admission?.patient || {};
  const doctorName =
    admission?.doctor?.full_name ||
    admission?.doctor?.name ||
    admission?.surgeon?.full_name ||
    admission?.surgeon?.name ||
    '-';

  return {
    ...buildPatientCoreFields(patient, hospital),
    surgeon_name: doctorName,
    admission_id: String(admission?.id || '-'),
    admission_date: formatDate(admission?.admit_date || admission?.admission_date),
    discharge_date: formatDate(admission?.discharge_date) || formatDate(dayjs()),
    doctor_name: doctorName,
    department: admission?.department || '-',
    bed_number: admission?.bed?.bed_number || admission?.bed_number || '-',
    room_name: admission?.bed?.room?.name || admission?.room?.name || '-',
    room_type: admission?.bed?.room?.room_type || admission?.room?.room_type || '-',
    floor_name:
      admission?.bed?.floor?.name ||
      admission?.bed?.room?.floor?.name ||
      admission?.floor?.name ||
      '-',
    procedure_name: '-',
    anesthetist_name: '-',
    nurse_name: '-',
    relative_name: admission?.attendee_name || admission?.attendee?.name || '-',
    relative_relationship: admission?.attendee_relationship || admission?.attendee?.relationship || '-',
    consent_datetime: formatDate(dayjs())
  };
};

/** Prefer admission fields when available; otherwise patient-only map. */
export const buildIpdVariableMap = ({ admission, patient, hospital } = {}) => {
  if (admission) return buildAdmissionVariableMap(admission, hospital);
  if (patient) return buildPatientVariableMap(patient, hospital);
  return {};
};

const EMPTY_VARIABLE_SPACER = '&nbsp;'.repeat(50);

export const applyIpdTemplateVariables = (html, variableMap) => {
  if (!html) return '';
  let result = html;
  Object.entries(variableMap || {}).forEach(([key, value]) => {
    const normalizedValue = value == null ? '' : String(value).trim();
    const safe = !normalizedValue || normalizedValue === '-' ? EMPTY_VARIABLE_SPACER : normalizedValue;
    result = result.replaceAll(`{{${key}}}`, safe);
  });
  return result;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * When the editor is showing patient-filled values, map those values back to
 * template tokens so source-template edits do not lose previously inserted
 * variables on the next refill/save cycle.
 */
export const restoreVariableTokensInHtml = (html, variableMap) => {
  if (!html) return '';

  const replacements = Object.entries(variableMap || {})
    .map(([key, value]) => ({
      token: `{{${key}}}`,
      value: value == null ? '' : String(value)
    }))
    .filter(({ value }) => value && value !== '-')
    .sort((a, b) => b.value.length - a.value.length);

  let result = html;
  replacements.forEach(({ token, value }) => {
    result = result.replace(new RegExp(escapeRegExp(value), 'g'), token);
  });

  return result;
};

export const parsePreferenceSettings = (settings) => {
  if (!settings) return {};
  if (typeof settings === 'string') {
    try {
      const parsed = JSON.parse(settings);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof settings === 'object' ? settings : {};
};

const normalizeCustomIpdTemplates = (templates) => {
  if (!Array.isArray(templates)) return [];

  return templates
    .map((template, index) => {
      if (!template || typeof template !== 'object') return null;

      const id = String(template.id || `ipd-tpl-${index}`);
      const name = String(template.name || '').trim();
      const content = typeof template.content === 'string' ? template.content : '';

      if (!name || !content.trim() || BUILTIN_TEMPLATE_IDS.has(id)) return null;

      return {
        id,
        name,
        content,
        created_at: template.created_at || null,
        updated_at: template.updated_at || null,
        is_builtin: false
      };
    })
    .filter(Boolean);
};

export const getCustomIpdReportTemplatesFromSettings = (settings) =>
  normalizeCustomIpdTemplates(parsePreferenceSettings(settings)?.[IPD_TEMPLATE_PREFERENCES_KEY]);

const buildMergedIpdTemplateList = (customTemplates = []) => [
  ...BUILTIN_IPD_TEMPLATES,
  ...normalizeCustomIpdTemplates(customTemplates)
];

const persistCustomIpdReportTemplates = async (customTemplates) => {
  const currentPrefs = await fetchUserPreferencesWithApp(true);
  const settings = parsePreferenceSettings(currentPrefs?.settings);
  const normalized = normalizeCustomIpdTemplates(customTemplates);

  await updateUserPreferencesWithApp(true, {
    settings: {
      ...settings,
      [IPD_TEMPLATE_PREFERENCES_KEY]: normalized
    }
  });

  return normalized;
};

export const loadIpdReportTemplates = async () => {
  try {
    const response = await fetchUserPreferencesWithApp(true);
    const sharedTemplates = getCustomIpdReportTemplatesFromSettings(response?.settings);
    return buildMergedIpdTemplateList(sharedTemplates);
  } catch {
    return buildMergedIpdTemplateList([]);
  }
};

export const saveIpdReportTemplate = async (name, content) => {
  const customOnly = (await loadIpdReportTemplates()).filter((t) => !t.is_builtin);
  const template = {
    id: `ipd-tpl-${Date.now()}`,
    name: name.trim(),
    content,
    created_at: new Date().toISOString(),
    is_builtin: false
  };
  await persistCustomIpdReportTemplates([...customOnly, template]);
  return template;
};

export const updateIpdReportTemplate = async (id, name, content) => {
  const customOnly = (await loadIpdReportTemplates()).filter((t) => !t.is_builtin);
  const updated = customOnly.map((template) =>
    String(template.id) === String(id)
      ? {
          ...template,
          name: name.trim(),
          content,
          updated_at: new Date().toISOString()
        }
      : template
  );
  const persisted = await persistCustomIpdReportTemplates(updated);
  return buildMergedIpdTemplateList(persisted);
};

export const deleteIpdReportTemplate = async (id) => {
  if (BUILTIN_TEMPLATE_IDS.has(id)) return loadIpdReportTemplates();
  const customOnly = (await loadIpdReportTemplates()).filter((t) => !t.is_builtin && t.id !== id);
  const persisted = await persistCustomIpdReportTemplates(customOnly);
  return buildMergedIpdTemplateList(persisted);
};

export const insertVariableToken = (html, key) => {
  const token = `{{${key}}}`;
  if (!html || html === '<p><br></p>') return `<p>${token}</p>`;
  if (html.endsWith('</p>')) {
    return html.replace(/<\/p>\s*$/, ` ${token}</p>`);
  }
  return `${html} ${token}`;
};

const PLACEHOLDER_RE = /\{\{([a-z_]+)\}\}/g;

const extractPlaceholderTokens = (html) => {
  if (!html) return [];
  return [...html.matchAll(PLACEHOLDER_RE)].map((match) => match[0]);
};

/**
 * If the stored template was overwritten with filled patient values, restore the
 * original placeholder template and keep any extra placeholders the user added.
 */
export const recoverPlaceholderTemplate = (currentHtml, baseTemplateHtml) => {
  if (!baseTemplateHtml) return currentHtml || '';
  if (!currentHtml) return baseTemplateHtml;

  const basePlaceholders = extractPlaceholderTokens(baseTemplateHtml);
  if (basePlaceholders.length === 0) return currentHtml;

  const currentPlaceholders = new Set(extractPlaceholderTokens(currentHtml));
  const baseStillIntact = basePlaceholders.every((token) => currentPlaceholders.has(token));
  if (baseStillIntact) return currentHtml;

  const baseTokenSet = new Set(basePlaceholders);
  const extraTokens = [...new Set(extractPlaceholderTokens(currentHtml).filter((t) => !baseTokenSet.has(t)))];

  let recovered = baseTemplateHtml;
  extraTokens.forEach((token) => {
    const key = token.slice(2, -2);
    recovered = insertVariableToken(recovered, key);
  });
  return recovered;
};

/** Keep user-added placeholders that exist in the editor but not yet in the template source. */
export const mergeExtraPlaceholdersFromEditor = (templateHtml, editorHtml) => {
  if (!templateHtml) return editorHtml || '';
  if (!editorHtml) return templateHtml;

  const templateTokenSet = new Set(extractPlaceholderTokens(templateHtml));
  const extras = [
    ...new Set(extractPlaceholderTokens(editorHtml).filter((token) => !templateTokenSet.has(token)))
  ];

  let merged = templateHtml;
  extras.forEach((token) => {
    merged = insertVariableToken(merged, token.slice(2, -2));
  });
  return merged;
};
